import { type Component, createSignal, createResource, For, Show } from 'solid-js'
import { A, useNavigate } from '@solidjs/router'
import { useKeywordFlagging } from '../../../shared/providers/coris/coris-keyword-flagging.provider'
import { useUser } from '../../../shared/providers/user-provider'
import { AppRoutes } from '../../../shared/app-routes'
import { Icon } from '@iconify-icon/solid'
import type { KeywordListSummary } from '../domain/models/keyword-list.model'
import Dialog from '../../../components/shared/components/Dialog/Dialog'
import { sticky } from '../../../shared/directives/sticky'

// Prevent tree-shaking of directive
sticky

const KeywordFlaggingPage: Component = () => {
  const navigate = useNavigate()
  const { getKeywordLists, deleteKeywordList } = useKeywordFlagging()
  const { organizationSlug } = useUser()
  const [search, setSearch] = createSignal('')
  const [deleteConfirmId, setDeleteConfirmId] = createSignal<string | null>(null)
  const [refreshKey, setRefreshKey] = createSignal(0)
  const [isStuck, setIsStuck] = createSignal(false)

  const [lists] = createResource(
    () => {
      const slug = organizationSlug()
      if (!slug) return null
      return { search: search(), refresh: refreshKey(), slug }
    },
    async (params) => {
      return await getKeywordLists.execute(params.search)
    }
  )

  const handleDelete = async (id: string): Promise<void> => {
    await deleteKeywordList.execute(id)
    setDeleteConfirmId(null)
    setRefreshKey((k) => k + 1)
  }

  const formatDate = (date: Date): string => {
    const d = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
    const t = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
    return `${d} ${t}`
  }

  return (
    <div class="flex w-full flex-col p-6">
      {/* Sticky filter bar — full-width background, content constrained */}
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 -mx-6 px-6 py-6 transition-colors duration-200"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent': !isStuck()
        }}
      >
        <div class="mx-auto w-full max-w-[1280px] flex items-start gap-8">
          {/* Search */}
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2.5 rounded-md border border-gray-200 bg-white px-3 py-2">
              <div class="text-gray-400">
                <Icon icon="heroicons:magnifying-glass" class="text-[1rem]" />
              </div>
              <input
                type="text"
                placeholder="Search by list name"
                value={search()}
                onInput={(e) => setSearch(e.currentTarget.value)}
                class="flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
              />
            </div>
          </div>
          <A
            href={AppRoutes.KeywordFlaggingNew()}
            class="flex-shrink-0 px-6 py-2 bg-white rounded-full shadow-sm border border-gray-200 flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            <Icon icon="heroicons:plus" class="text-[1rem]" />
            Add Advanced Filter
          </A>
        </div>
      </div>

      {/* Content — constrained */}
      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-8 pt-6">
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table class="min-w-full divide-y divide-gray-200">
            <thead>
              <tr class="bg-gray-50">
                <th class="px-4 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-b border-gray-200">
                  List Name
                </th>
                <th class="px-4 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-b border-gray-200 max-w-56">
                  Items
                </th>
                <th class="px-4 py-3.5 text-left text-sm font-medium text-gray-900 border-r border-b border-gray-200 max-w-64">
                  Date Modified
                </th>
                <th class="px-4 py-3.5 text-center text-sm font-medium text-gray-900 border-r border-b border-gray-200 w-28">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <Show
                when={!lists.loading}
                fallback={
                  <For each={[1, 2, 3]}>
                    {() => (
                      <tr class="animate-pulse">
                        <td class="px-4 py-3 border-r border-gray-200">
                          <div class="h-5 bg-gray-200 rounded w-32" />
                        </td>
                        <td class="px-4 py-3 border-r border-gray-200 max-w-56">
                          <div class="h-5 bg-gray-200 rounded w-8" />
                        </td>
                        <td class="px-4 py-3 border-r border-gray-200 max-w-64">
                          <div class="h-5 bg-gray-200 rounded w-36" />
                        </td>
                        <td class="px-4 py-3 border-r border-gray-200 w-28">
                          <div class="h-5 bg-gray-200 rounded w-8 mx-auto" />
                        </td>
                      </tr>
                    )}
                  </For>
                }
              >
                <For
                  each={lists()}
                  fallback={
                    <tr>
                      <td colspan="4" class="px-4 py-12 text-center text-sm text-gray-500">
                        No keyword lists found.
                      </td>
                    </tr>
                  }
                >
                  {(item: KeywordListSummary) => (
                    <tr
                      class="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(AppRoutes.KeywordFlaggingEditId(item.id))}
                    >
                      <td class="px-4 py-3 border-r border-b border-gray-200">
                        <div class="flex items-center gap-2">
                          <span class="text-sm font-medium text-gray-700">{item.name}</span>
                          <Show when={item.autoModeration}>
                            <span class="inline-flex items-center gap-1.5 text-primary-600">
                              <Icon icon="heroicons:cpu-chip" class="text-[1rem]" />
                              <span class="text-xs font-medium">Automoderation</span>
                            </span>
                          </Show>
                        </div>
                      </td>
                      <td class="px-4 py-3 border-r border-b border-gray-200 max-w-56">
                        <span class="text-sm text-gray-700">{item.itemCount}</span>
                      </td>
                      <td class="px-4 py-3 border-r border-b border-gray-200 max-w-64">
                        <span class="text-sm text-gray-700">{formatDate(item.dateModified)}</span>
                      </td>
                      <td class="px-4 py-3 border-r border-b border-gray-200 w-28">
                        <div class="flex justify-center items-center gap-2">
                          <button
                            type="button"
                            class="w-8 h-8 rounded-md flex items-center justify-center text-gray-500 border border-transparent hover:border-gray-200 hover:shadow-sm transition-all"
                            onClick={(e) => {
                              e.stopPropagation()
                              setDeleteConfirmId(item.id)
                            }}
                            title="Delete"
                          >
                            <Icon icon="heroicons:trash" class="text-[1rem]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Show when={deleteConfirmId()}>
        <Dialog maxWidth="24rem">
          <div class="p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-2">Delete Keyword List</h3>
            <p class="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this keyword list? This action cannot be undone.
            </p>
            <div class="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirmId()!)}
                class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </Dialog>
      </Show>
    </div>
  )
}

export default KeywordFlaggingPage
